import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Bell, Mail, X, Check, Trophy, Zap, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscribeModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    new_challenges: true,
    scheduled_challenges: true,
    winners_announced: true,
    challenge_ending_soon: false
  });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email, preferences });
      setSubscribed(true);
      toast.success('🎉 Successfully subscribed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 z-[9999] bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ minHeight: '100vh', minHeight: '100dvh' }}
    >
      <div className="min-h-full flex items-center justify-center p-4 py-20">
        <Card className="relative w-full max-w-md bg-zinc-900 border-red-500/30 p-6 animate-in fade-in zoom-in-95 duration-200 shadow-2xl shadow-red-500/20">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-500 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {subscribed ? (
          /* Success State */
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">You're Subscribed!</h2>
            <p className="text-gray-400 mb-6">You'll receive notifications for new challenges and winners.</p>
            <Button onClick={onClose} className="bg-green-600 hover:bg-green-700">
              Got it!
            </Button>
          </div>
        ) : (
          /* Subscribe Form */
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-red-500/20 rounded-xl">
                <Bell className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  Stay Updated
                </h2>
                <p className="text-sm text-gray-500">Get notified about challenges & winners</p>
              </div>
            </div>

            <form onSubmit={handleSubscribe} className="space-y-4">
              {/* Email Input */}
              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 bg-black/50 border-gray-700 focus:border-red-500 h-12"
                    data-testid="subscribe-email-input"
                    required
                  />
                </div>
              </div>

              {/* Preferences */}
              <div className="space-y-2">
                <p className="text-sm text-gray-400 mb-2">Notification preferences:</p>
                
                <label className="flex items-center gap-3 p-3 bg-black/30 rounded-lg cursor-pointer hover:bg-black/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={preferences.new_challenges}
                    onChange={() => togglePreference('new_challenges')}
                    className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500 bg-black"
                  />
                  <Zap className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-white">Challenge goes live</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-black/30 rounded-lg cursor-pointer hover:bg-black/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={preferences.scheduled_challenges}
                    onChange={() => togglePreference('scheduled_challenges')}
                    className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500 bg-black"
                  />
                  <Calendar className="w-4 h-4 text-orange-400" />
                  <span className="text-sm text-white">New challenge scheduled</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-black/30 rounded-lg cursor-pointer hover:bg-black/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={preferences.winners_announced}
                    onChange={() => togglePreference('winners_announced')}
                    className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500 bg-black"
                  />
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-white">Winners announced</span>
                </label>

                <label className="flex items-center gap-3 p-3 bg-black/30 rounded-lg cursor-pointer hover:bg-black/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={preferences.challenge_ending_soon}
                    onChange={() => togglePreference('challenge_ending_soon')}
                    className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500 bg-black"
                  />
                  <Clock className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-white">Challenge ending soon (24h reminder)</span>
                </label>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                className="w-full h-12 bg-red-600 hover:bg-red-700 font-bold"
                disabled={loading}
                data-testid="subscribe-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Subscribing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Subscribe
                  </span>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                You can unsubscribe anytime. We respect your privacy.
              </p>

              {/* Social Links Divider */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 h-px bg-gray-700"></div>
                <span className="text-xs text-gray-500">or follow us on</span>
                <div className="flex-1 h-px bg-gray-700"></div>
              </div>

              {/* Social Buttons */}
              <div className="flex gap-3">
                {/* Discord Button */}
                <a
                  href="https://discord.gg/TsqcHNm647"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 p-3 bg-[#5865F2]/20 hover:bg-[#5865F2]/30 border border-[#5865F2]/30 rounded-lg transition-colors"
                  data-testid="subscribe-discord-btn"
                >
                  <svg className="w-5 h-5 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  <span className="text-sm font-medium text-[#5865F2]">Discord</span>
                </a>

                {/* Twitter/X Button */}
                <a
                  href="https://x.com/CesarMChallenge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 p-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
                  data-testid="subscribe-twitter-btn"
                >
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="text-sm font-medium text-white">Twitter/X</span>
                </a>
              </div>
            </form>
          </>
        )}
      </Card>
      </div>
    </div>
  );
};

export default SubscribeModal;
